import { useApiListIamRolesWithUsers } from '@repo/api-codegen';
import type { LiteUserOut } from '@repo/api-codegen/v2';
import { fetchApiGetEntityStakeholdersV2 } from '@repo/api-codegen/v2';
import { useQueries } from '@tanstack/react-query';
import {
	useUserList,
	type ISecodaEntity,
	type ISlackChannel,
	type IUser,
	type IUserGroup,
} from '../../api';
import { useExtendedUserList } from '../../api/hooks/user/useExtendedUserList';
import { useSlackChannels } from '../../api/hooks/workspace/useSlackChannels';
import { getDisplayName } from '../../utils/userUtils';
import type { AnnouncementRelatedResourceItem } from './AnnouncementRelatedResource';
import { selectChannelMap, selectGroupMap, selectUserMap } from './helpers';

type GroupType =
	| 'Workspace admins'
	| 'Workspace editors'
	| 'Workspace viewers'
	| 'Other groups'
	| 'Other slack channels'
	| 'Resource recipients'
	| string; // Allow for dynamic custom role group names

export const useAnnouncementGlobalRecipientsList = () => {
	const { activeUsers: usersData, userGroups: groupsData } =
		useExtendedUserList();
	const { data: channelsData } = useSlackChannels({});
	const { data: iamRolesWithUsers } = useApiListIamRolesWithUsers({});

	const users = (usersData ?? []) as unknown as IUser[];
	const groups = (groupsData ?? []) as unknown as IUserGroup[];
	const channels = (channelsData ?? []) as unknown as ISlackChannel[];
	const customRoles = (iamRolesWithUsers?.results || []).filter(
		(role) => !role.is_legacy
	);

	const optionsAdmins = users
		.filter((u) => u.role === 'admin')
		.map(selectUserMap<GroupType>('Workspace admins'));

	const optionsEditors = users
		.filter((u) => u.role === 'editor')
		.map(selectUserMap<GroupType>('Workspace editors'));

	const optionsViewers = users
		?.filter((u) => u.role === 'viewer')
		.map(selectUserMap<GroupType>('Workspace viewers'));

	const optionsGroups = groups?.map(selectGroupMap<GroupType>('Other groups'));

	const optionsChannels = channels?.map(
		selectChannelMap<GroupType>('Other slack channels')
	);

	const optionsCustomRoles = customRoles.flatMap((role) => {
		const groupLabel = `Workspace ${role.name}`;

		const usersInThisRole = users.filter((user) =>
			role.users.some((roleUser) => roleUser.id === user.id)
		);

		return usersInThisRole.map(selectUserMap<GroupType>(groupLabel));
	});

	return {
		channels,
		users,
		groups,
		options: [
			...optionsAdmins,
			...optionsEditors,
			...optionsViewers,
			...optionsGroups,
			...optionsChannels,
			...optionsCustomRoles,
		],
	};
};

const ROLES = [
	{ label: 'Admins', value: 'admin' },
	{ label: 'Editors', value: 'editor' },
	{ label: 'Viewers', value: 'viewer' },
	{ label: 'Downstream users', value: 'downstream_users' },
	{ label: 'Frequent users', value: 'frequent_users' },
	{ label: 'Frequent queriers', value: 'frequent_queriers' },
];

export const useInternalUsers = (entities: ISecodaEntity[]) => {
	const { data: usersData } = useUserList({});

	const internalUsers =
		usersData?.results?.filter((user) =>
			entities.some((entity) => entity?.internal_users?.includes(user.id))
		) ?? [];

	return {
		internalUsers,
	};
};

export const useExternalUsers = (entities: ISecodaEntity[]) => {
	const { data: serviceAccountsData } = useUserList({
		filters: {
			is_service_account: true,
		},
	});

	const { data: usersData } = useUserList({
		filters: {
			is_service_account: false,
		},
	});

	const withPopularity =
		serviceAccountsData?.results
			?.filter((sa) => sa.service_account_count_popularity)
			.concat(usersData?.results ?? [])
			?.filter((user) =>
				entities.some((entity) => entity?.external_users?.includes(user.id))
			) ?? [];

	const withPopularityAndValidEmail = withPopularity.filter(
		(u: { email: string | string[] }) =>
			!u.email.includes('gserviceaccount.com')
	);

	return {
		withPopularity,
		withPopularityAndValidEmail,
	};
};

export const useAnnouncementRecipientsList = (
	selectedData: AnnouncementRelatedResourceItem[]
) => {
	const { activeUsers: usersData, userGroups: groupsData } =
		useExtendedUserList();
	const { data: channelsData } = useSlackChannels({});

	const stakeholdersQueries = useQueries({
		queries: selectedData.map((item) => ({
			queryKey: ['apiGetEntityStakeholdersV2', { entityId: item.entity.id }],
			queryFn: () =>
				fetchApiGetEntityStakeholdersV2({
					pathParams: { entityId: item.entity.id },
				}),
		})),
	});

	const downstreamUserIds = stakeholdersQueries.flatMap((query) => {
		if (query.data?.downstream_users) {
			return query.data.downstream_users.map((user: LiteUserOut) => user.id);
		}
		return [];
	});
	const uniqueDownstreamUserIds = [...new Set(downstreamUserIds)];

	const { internalUsers } = useInternalUsers(
		selectedData.map((item) => item.entity)
	);
	const uniqueFrequentUsers = [
		...new Set([...internalUsers.map((user) => user.id)]),
	];

	const { withPopularityAndValidEmail } = useExternalUsers(
		selectedData.map((item) => item.entity)
	);
	const uniqueFrequentQueriers = [
		...new Set([...withPopularityAndValidEmail.map((user) => user.id)]),
	];

	const roleToUserIds: Record<string, string[]> = {};
	ROLES.forEach((role) => {
		if (role.value === 'downstream_users') {
			roleToUserIds[role.value] = uniqueDownstreamUserIds;
		} else if (role.value === 'frequent_users') {
			roleToUserIds[role.value] = uniqueFrequentUsers;
		} else if (role.value === 'frequent_queriers') {
			roleToUserIds[role.value] = uniqueFrequentQueriers;
		} else {
			roleToUserIds[role.value] = usersData
				.filter((user) => user.role === role.value)
				.map((user) => user.id);
		}
	});

	const rolesToInclude = ROLES.filter((role) => {
		const userIds = roleToUserIds[role.value];
		return userIds && userIds.length > 0;
	});

	const ownerToSuffixMap = new Map<string, string>();

	selectedData.forEach((item) => {
		(item.entity.owners ?? []).forEach((ownerId) => {
			if (!ownerToSuffixMap.get(ownerId) && item.entity.title) {
				ownerToSuffixMap.set(ownerId, item.entity.title);
			}
		});
		(item.entity.owners_groups ?? []).forEach((ownerGroupId) => {
			if (!ownerToSuffixMap.get(ownerGroupId) && item.entity.title) {
				ownerToSuffixMap.set(ownerGroupId, item.entity.title);
			}
		});
		(item.entity.display_metadata?.owners ?? []).forEach((ownerId) => {
			if (!ownerToSuffixMap.get(ownerId.id) && item.entity.title) {
				ownerToSuffixMap.set(ownerId.id, item.entity.title);
			}
		});
		(item.entity.display_metadata?.owners_groups ?? []).forEach(
			(ownerGroupId) => {
				if (!ownerToSuffixMap.get(ownerGroupId.id) && item.entity.title) {
					ownerToSuffixMap.set(ownerGroupId.id, item.entity.title);
				}
			}
		);
	});

	const suggestedOwnerIds = [
		...new Set(
			selectedData.flatMap((item) => [
				...(item.entity.owners || []),
				...(item.entity.display_metadata?.owners?.map((owner) => owner.id) ||
					[]),
			])
		),
	];
	const suggestedGroupIds = [
		...new Set(
			selectedData.flatMap((item) => [
				...(item.entity.owners_groups || []),
				...(item.entity.display_metadata?.owners_groups?.map(
					(group) => group.id
				) || []),
			])
		),
	];

	const groups = groupsData ?? [];
	const channels = channelsData ?? [];

	return [
		...usersData
			.filter((user) => suggestedOwnerIds.includes(user.id))
			.map((user) => {
				const suffix = ownerToSuffixMap.get(user.id);
				return {
					label: getDisplayName(user),
					value: `user_id:${user.id}`,
					group: 'Suggested' as const,
					type: 'user' as const,
					recipient: user,
					...(suffix ? { suffix: `Owner of ${suffix}` } : {}),
				};
			}),
		...groups
			.filter((group) => suggestedGroupIds.includes(group.id))
			.map((group) => {
				const suffix = ownerToSuffixMap.get(group.id);
				return {
					label: group.name,
					value: `user_group_id:${group.id}`,
					group: 'Suggested' as const,
					type: 'group' as const,
					recipient: group,
					...(suffix ? { suffix: `Owner of ${suffix}` } : {}),
				};
			}),
		...rolesToInclude.map((role) => ({
			label: role.label,
			value: `role:${role.value}`,
			group: 'Roles' as const,
			role: role.label,
			userIds: roleToUserIds[role.value],
		})),
		...groups.map((group) => ({
			label: group.name,
			value: `user_group_id:${group.id}`,
			group: 'Groups' as const,
			userGroup: group,
		})),
		...usersData.map((user) => ({
			label: getDisplayName(user),
			value: `user_id:${user.id}`,
			group: 'Members' as const,
			user: user,
		})),
		...channels.map((channel) => ({
			label: channel.name,
			value: `channel_id:${channel.id}`,
			group: 'Slack Channels' as const,
			channel: channel,
		})),
	];
};
